import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { CommandHistoryEntry } from './command-bar-types.js';

const MAX_HISTORY_SIZE = 50;

export interface CommandBarStore {
	// UI State
	isOpen: boolean;
	search: string;
	searchPrefix: string | null;

	// History State
	history: CommandHistoryEntry[];
	historyIndex: number; // -1 means not navigating history

	// Actions
	open: () => void;
	close: () => void;
	toggle: () => void;
	setSearch: (search: string) => void;

	// History Actions
	addToHistory: (query: string) => void;
	navigateHistoryUp: () => string | null;
	navigateHistoryDown: () => string | null;
	resetHistoryNavigation: () => void;
}

export const useCommandBarStore = create<CommandBarStore>()(
	persist(
		(set, get) => ({
			// Initial UI State
			isOpen: false,
			search: '',
			searchPrefix: null,

			// Initial History State
			history: [],
			historyIndex: -1,

			// UI Actions
			open: () => set({ isOpen: true, historyIndex: -1 }),
			close: () => set({ isOpen: false, search: '', historyIndex: -1 }),
			toggle: () => {
				const { isOpen } = get();
				if (isOpen) {
					set({ isOpen: false, search: '', historyIndex: -1 });
				} else {
					set({ isOpen: true, historyIndex: -1 });
				}
			},
			setSearch: (search) => set({ search, historyIndex: -1 }),

			// History Actions
			addToHistory: (query) => {
				if (!query.trim()) {
					return;
				}

				set((state) => {
					// Don't add duplicates at the end
					if (state.history[0]?.query === query) {
						return state;
					}

					const newHistory = [
						{ query, timestamp: Date.now() },
						...state.history.filter((h) => h.query !== query),
					].slice(0, MAX_HISTORY_SIZE);

					return { history: newHistory };
				});
			},

			navigateHistoryUp: () => {
				const { history, historyIndex } = get();
				if (history.length === 0) {
					return null;
				}

				const newIndex = Math.min(historyIndex + 1, history.length - 1);
				set({ historyIndex: newIndex });
				return history[newIndex]?.query ?? null;
			},

			navigateHistoryDown: () => {
				const { history, historyIndex } = get();
				if (historyIndex <= 0) {
					set({ historyIndex: -1 });
					return ''; // Return empty string to clear input
				}

				const newIndex = historyIndex - 1;
				set({ historyIndex: newIndex });
				return history[newIndex]?.query ?? null;
			},

			resetHistoryNavigation: () => set({ historyIndex: -1 }),
		}),
		{
			name: 'command-bar-history',
			partialize: (state) => ({ history: state.history }),
		},
	),
);
